import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// DraftAssist — premium polish for the dispute workbench.
// Three sub-components, all additive:
//   <ThinkingPill busy="..."/>     — replaces the "Drafting…" string with a real animation
//   <DraftMeter text=" "/>         — char count + "good length" hint, real-time
//   <CopyButton text=" "/>         — copy with success morph + auto-revert
// Bundle cost: ~1.1 KB gzipped.
// Accessibility: live region for status, button labels, keyboard parity, no hover-only.
import { useEffect, useState } from 'react';
import { Button } from '../../ui/Button';
import { Check, Copy, Sparkles } from 'lucide-react';
export function ThinkingPill({ busy, label }) {
    if (!busy)
        return null;
    return (_jsxs("span", { role: "status", "aria-live": "polite", className: "inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-100", children: [_jsx("span", { "aria-hidden": "true", className: "relative inline-flex h-2 w-2", children: _jsx("span", { className: "absolute inset-0 rounded-full bg-brand-500 rr-pulse-dot" }) }), _jsx(Sparkles, { size: 12, "aria-hidden": "true" }), _jsx("span", { children: label }), _jsx("span", { "aria-hidden": "true", className: "ml-1 inline-block h-3 w-[1px] bg-brand-700 rr-caret" })] }));
}
export function DraftMeter({ text, idealMin = 220, idealMax = 600 }) {
    const len = text?.length ?? 0;
    const status = len === 0 ? 'empty' :
        len < idealMin ? 'short' :
            len > idealMax ? 'long' :
                'ok';
    const pct = Math.min(100, Math.round((len / idealMax) * 100));
    const colorBar = status === 'ok' ? 'bg-emerald-500' :
        status === 'long' ? 'bg-amber-500' :
            'bg-slate-400';
    const labelText = status === 'empty' ? 'Empty' :
        status === 'short' ? `${len} chars · still short — aim for ${idealMin}–${idealMax}` :
            status === 'long' ? `${len} chars · long — aim for ${idealMin}–${idealMax}` :
                `${len} chars · good length`;
    return (_jsxs("div", { className: "mt-2 space-y-1.5", "aria-live": "polite", children: [_jsxs("div", { className: "flex items-center justify-between text-[11px] text-slate-500", children: [_jsx("span", { children: labelText }), _jsxs("span", { className: "tabular-nums", children: [len, "/", idealMax] })] }), _jsx("div", { role: "progressbar", "aria-valuemin": 0, "aria-valuemax": idealMax, "aria-valuenow": len, "aria-label": "Draft length", className: "h-1 w-full overflow-hidden rounded-full bg-slate-100", children: _jsx("div", { className: `h-full transition-[width] duration-300 ease-out ${colorBar}`, style: { width: `${pct}%` } }) })] }));
}
export function CopyButton({ text, label = 'Copy' }) {
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!copied)
            return;
        const t = setTimeout(() => setCopied(false), 1600);
        return () => clearTimeout(t);
    }, [copied]);
    async function onClick() {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
        }
        catch {
            // Clipboard may be blocked (e.g. insecure context); silently no-op.
            // Caller's existing flow can still surface a toast.
        }
    }
    return (_jsx(Button, { variant: "secondary", onClick: onClick, className: "rr-press-ok inline-flex items-center gap-2", "aria-live": "polite", "aria-label": copied ? 'Copied to clipboard' : label, children: copied ? (_jsxs(_Fragment, { children: [_jsx(Check, { size: 16, "aria-hidden": "true", className: "text-emerald-600 rr-bounce-in" }), _jsx("span", { className: "text-emerald-700", children: "Copied" })] })) : (_jsxs(_Fragment, { children: [_jsx(Copy, { size: 16, "aria-hidden": "true" }), _jsx("span", { children: label })] })) }));
}
